import React, { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import { UploadDocumentModal } from '../../components/DocumentLibrary/UploadDocumentModal'
import { DocumentsTable } from '../../components/DocumentLibrary/DocumentsTable'
import { StorageQuotaDisplay } from '../../components/DocumentLibrary/StorageQuotaDisplay'
import { toast } from 'react-toastify'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  BulkDownloadProvider,
  BulkDownloadWidget,
} from '../../features/bulk-downloads'
import { featureFlagService } from 'utils/featureFlagService'

const TABLE_CONTAINER_HEIGHT = 'calc(var(--base-page-table-height) - 95px)'

export const DocLibraryPage: React.FC = () => {
  const { enableNewNavigation } = featureFlagService()
  const { preferences } = usePreferences()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const selectedTab = location.pathname.split('/')[2] || 'team'

  useEffect(() => {
    if (!location.pathname.includes('/documents/')) {
      navigate('/documents/team')
    }
  }, [location.pathname, navigate])

  // Check feature flag
  if (!preferences?.feature_flags?.enable_document_library) {
    return <div>Document Library is not enabled</div>
  }

  const handleUploadSuccess = () => {
    toast.success('Document uploaded successfully')
    // Invalidate all document table queries to refresh the tables
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' &&
        (query.queryKey[0] as string).startsWith('DOCUMENTS_'),
    })
    // Invalidate storage quota query
    queryClient.invalidateQueries({ queryKey: ['documents', 'quota'] })
  }

  const tabs = [
    { key: 'team', title: 'Team' },
    { key: 'product', title: 'Product' },
    { key: 'company', title: 'Company' },
  ]

  return (
    <BulkDownloadProvider>
      <FBPage>
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>Documents</FBPageTitle>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
              }}
            >
              <StorageQuotaDisplay inline />
              <UploadDocumentModal onSuccess={handleUploadSuccess} />
            </div>
          </FBPageTitleRow>
        </FBPageHeader>

        <FBPageInnerTabsContainer>
          <Tabs
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={(value) => {
              navigate(`/documents/${value}`)
            }}
            tabStyle={{ width: '125px' }}
          />
        </FBPageInnerTabsContainer>

        <FBPageTableContainer
          style={
            enableNewNavigation ? undefined : { height: TABLE_CONTAINER_HEIGHT }
          }
        >
          {selectedTab === 'team' && (
            <DocumentsTable
              tableKey="DOCUMENTS_TEAM_TABLE"
              filterIdentifier="DOCUMENTS_TEAM_TABLE"
              tabType="team"
            />
          )}
          {selectedTab === 'product' && (
            <DocumentsTable
              tableKey="DOCUMENTS_PRODUCT_TABLE"
              filterIdentifier="DOCUMENTS_PRODUCT_TABLE"
              tabType="product"
            />
          )}
          {selectedTab === 'company' && (
            <DocumentsTable
              tableKey="DOCUMENTS_COMPANY_TABLE"
              filterIdentifier="DOCUMENTS_COMPANY_TABLE"
              tabType="company"
            />
          )}
        </FBPageTableContainer>
      </FBPage>
      <BulkDownloadWidget />
    </BulkDownloadProvider>
  )
}
